import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useAuthState, useSignOut } from "react-firebase-hooks/auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebaseConfig";

export function useAuth() {
  const [authUser, authLoading, error] = useAuthState(auth);
  const [isLoading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const ref = doc(db, "users", authUser.uid);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        setUser(docSnap.data());
        localStorage.setItem("user", JSON.stringify(docSnap.data()));
      }
      setLoading(false);
    }

    if (!authLoading) {
      if (authUser) fetchData();
      else setLoading(false); // Not signed in
    }
  }, []);

  return { user, isLoading, error };
}
export const useLogin = () => {
  const [isLoading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    message: "",
  });
  const navigate = useNavigate();

  const login = async ({ email, password, redirectTo }) => {
    if (!email || !password) {
      return setToast({
        open: true,
        type: "error",
        message: "Invalid field value",
      });
    }
    setLoading(true);
    try {
      const user = await signInWithEmailAndPassword(auth, email, password);
      setToast({
        open: true,
        type: "success",
        message: "You are logged in",
      });
      localStorage.setItem("user", JSON.stringify(user?.user));
      navigate(redirectTo);
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        message: error.message || "Logging in failed",
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return { login, isLoading, toast, setToast };
};

export function useRegister() {
  const [isLoading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    message: "",
  });
  const navigate = useNavigate();

  async function register({
    firstName,
    lastName,
    email,
    password,
    redirectTo = "/checkout",
  }) {
    if (!firstName || !lastName || !email || !password) {
      return setToast({
        open: true,
        type: "error",
        message: "Invalid field value",
      });
    }

    setLoading(true);

    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, "users", res.user.uid), {
        uid: res.user.uid,
        name: firstName + " " + lastName,
        email,
        role: "user",
        timestamp : new Date()
      });

      setToast({
        message: "You are logged in",
        type: "success",
        open: true,
      });
      localStorage.setItem("user", JSON.stringify(res?.user));
      navigate(redirectTo);
    } catch (error) {
      setToast({
        message: error.message,
        type: "error",
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }

  return { register, isLoading, toast, setToast };
}

export function useLogout() {
  const [signOut, isLoading, error] = useSignOut(auth);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    message: "",
  });

  async function logout() {
    if (await signOut()) {
      setToast({
        message: "Successfully logged out",
        type: "success",
        open: true,
      });
      localStorage.removeItem("user");
      localStorage.removeItem("subscription");
      // window.location.reload();
    } // else: show error [signOut() returns false if failed]
  }

  return { logout, isLoading, toast, setToast };
}

export const useForgotPassword = () => {
  const [isLoading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    message: "",
  });

  const forgotPassword = async ({ email }) => {
    if (!email) {
      return setToast({
        open: true,
        type: "error",
        message: "Invalid field value",
      });
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setToast({
        open: true,
        type: "success",
        message: "We've sent a reset password link to your email",
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        message: error.message || "Someting went wrong",
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return { forgotPassword, isLoading, toast, setToast };
};

export const useUpdateAccount = () => {
  const [isLoading, setLoading] = useState(false);
  const [authUser, authLoading, error] = useAuthState(auth);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    message: "",
  });

  const updateAccount = async ({ name, email, password }) => {
    setLoading(true);

    const promptForCredentials = () => {
      // const email = prompt("Enter your email");
      const password = prompt("Enter your password");

      return password;
    };

    const credential = EmailAuthProvider.credential(
      authUser.email,
      promptForCredentials()
    );

    try {
      await reauthenticateWithCredential(authUser, credential);
      await updateEmail(authUser, email);
      if (password) {
        await updatePassword(authUser, password);
      }
      const updateRef = doc(db, "users", authUser.uid);
      await updateDoc(updateRef, {
        name,
        email,
      });
      const ref = doc(db, "users", authUser.uid);
      const docSnap = await getDoc(ref);
      localStorage.setItem("user", JSON.stringify(docSnap.data()));
      setToast({
        open: true,
        type: "success",
        message: "Account update successfully",
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        message: error.message || "Someting went wrong",
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return { updateAccount, isLoading, toast, setToast };
};
